/**
 * MONITORING HELPERS — Calculs scientifiques
 *
 * ACWR calculé via EWMA (Exponentially Weighted Moving Average)
 * conformément à Williams et al. (2017) et Murray et al. (2017).
 *
 * Hooper Score : Hooper & Mackinnon (1995)
 * Training Load : Foster sRPE method (2001)
 * Strain/Monotonie : Foster (1998)
 */

import type { TrainingLoadRow, DailyWellnessRow, SessionSetLogRow, ProgrammePlanSession } from "@/lib/supabase"
import { toLocalISODate, startOfWeek } from "@/lib/utils"

// ============================================
// HOOPER SCORE
// ============================================

export type HooperZone = "excellent" | "bon" | "moyen" | "mauvais" | "très mauvais"

export function getHooperZone(score: number): {
  zone: HooperZone
  label: string
  color: string
  description: string
} {
  if (score <= 7) {
    return { zone: "excellent", label: "Excellent", color: "#22c55e", description: "Forme excellente. Continue ainsi." }
  }
  if (score <= 15) {
    return { zone: "bon", label: "Bon", color: "#06b6d4", description: "Forme correcte. Surveille la récupération." }
  }
  if (score <= 19) {
    return { zone: "moyen", label: "Moyen", color: "#eab308", description: "Fatigue modérée. Réduis l'intensité si besoin." }
  }
  if (score <= 23) {
    return { zone: "mauvais", label: "Mauvais", color: "#f97316", description: "Fatigue notable. Réduis la charge." }
  }
  return { zone: "très mauvais", label: "Très mauvais", color: "#ef4444", description: "Risque élevé. Repos recommandé." }
}

// ============================================
// TRAINING LOAD
// ============================================

export function computeTrainingLoad(rpe: number, durationMinutes: number): number {
  return Math.round(rpe * durationMinutes)
}

// ============================================
// EWMA (Exponentially Weighted Moving Average)
// ============================================

/**
 * Calcule l'EWMA sur une série de charges quotidiennes.
 *
 * EWMA(t) = Charge(t) × λ + EWMA(t-1) × (1 - λ)
 * λ = 2 / (N + 1)
 *
 * On remplit les jours sans entraînement avec une charge de 0.
 * L'initialisation se fait sur la première valeur (comme dans la littérature).
 *
 * Ref: Williams S. et al. (2017) "Better way to determine ACWR"
 */
function computeEWMA(
  dailyLoads: { date: string; load: number }[],
  windowDays: number
): number {
  if (dailyLoads.length === 0) return 0

  const lambda = 2 / (windowDays + 1)

  // Initialiser avec la première valeur
  let ewma = dailyLoads[0].load

  for (let i = 1; i < dailyLoads.length; i++) {
    ewma = dailyLoads[i].load * lambda + ewma * (1 - lambda)
  }

  return ewma
}

/**
 * Construit une série quotidienne continue (sans trous) à partir des loads.
 * Les jours sans entraînement reçoivent une charge de 0.
 */
function buildDailySeries(
  loads: TrainingLoadRow[],
  totalDays: number
): { date: string; load: number }[] {
  const today = new Date()
  const series: { date: string; load: number }[] = []

  const loadMap = new Map<string, number>()
  for (const l of loads) {
    loadMap.set(l.date, l.training_load || 0)
  }

  for (let i = totalDays - 1; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const iso = toLocalISODate(d)
    series.push({
      date: iso,
      load: loadMap.get(iso) ?? 0,
    })
  }

  return series
}

// ============================================
// ACWR (EWMA method — Williams et al., 2017)
// ============================================

/**
 * ACWR = EWMA_acute (λ=2/8=0.25) / EWMA_chronic (λ=2/29≈0.069)
 *
 * Zones (Gabbett 2016, confirmées par Hulin et al. 2016) :
 * - < 0.8  : sous-charge (désentraînement possible)
 * - 0.8-1.3 : sweet spot (progression optimale)
 * - 1.3-1.5 : surcharge modérée (vigilance)
 * - > 1.5  : zone à risque (×2 à ×5 risque blessure)
 *
 * Avantages EWMA vs rolling average :
 * - Pondère davantage les charges récentes
 * - Lisse mieux les pics ponctuels
 * - Plus sensible aux changements rapides de charge
 * - Pas d'effet "cliff" quand un jour sort de la fenêtre
 */
export type AcwrZone = "sous-entrainement" | "optimal" | "vigilance" | "surcharge"

export function computeACWR(loads: TrainingLoadRow[]): {
  acwr: number
  acuteEWMA: number
  chronicEWMA: number
  zone: AcwrZone
  label: string
  color: string
  description: string
} {
  if (loads.length === 0) {
    return {
      acwr: 0,
      acuteEWMA: 0,
      chronicEWMA: 0,
      zone: "sous-entrainement",
      label: "Pas de données",
      color: "#94a3b8",
      description: "Démarre par enregistrer tes premières séances.",
    }
  }

  // On construit 56 jours continus : l'EWMA chronique (λ≈0,069) initialisée sur le premier jour
  // garde encore ~15 % de son poids sur ce jour après seulement 28 itérations — sans ces 28 jours
  // d'amorçage supplémentaires, le chronique reste biaisé par la valeur exacte d'il y a 28 jours.
  const dailySeries = buildDailySeries(loads, 56)

  // λ_acute = 2/(7+1) = 0.25, λ_chronic = 2/(28+1) ≈ 0.069
  const acuteEWMA = computeEWMA(dailySeries, 7)
  const chronicEWMA = computeEWMA(dailySeries, 28)

  const acwr = chronicEWMA > 0 ? acuteEWMA / chronicEWMA : 0

  let zone: AcwrZone, label: string, color: string, description: string
  if (acwr < 0.8) {
    zone = "sous-entrainement"
    label = "Sous-entraînement"
    color = "#eab308"
    description = "Charge insuffisante. Tu peux progresser plus vite."
  } else if (acwr <= 1.3) {
    zone = "optimal"
    label = "Optimal"
    color = "#22c55e"
    description = "Zone idéale (sweet spot). Progression saine."
  } else if (acwr <= 1.5) {
    zone = "vigilance"
    label = "Vigilance"
    color = "#f97316"
    description = "Surcharge modérée. Vigilance recommandée."
  } else {
    zone = "surcharge"
    label = "Surcharge"
    color = "#ef4444"
    description = "Risque blessure élevé (×2 à ×5). Réduis la charge."
  }

  return {
    acwr: Math.round(acwr * 100) / 100,
    acuteEWMA: Math.round(acuteEWMA),
    chronicEWMA: Math.round(chronicEWMA),
    zone,
    label,
    color,
    description,
  }
}

/**
 * Série ACWR historique (un point par jour), même méthode EWMA que computeACWR
 * mais en conservant le ratio acute/chronic à chaque pas plutôt que le dernier seul.
 * `lookbackDays` jours supplémentaires sont calculés en amont pour amorcer l'EWMA
 * chronique (λ≈0,069) avant le premier jour réellement retourné — 56 jours, pas 28,
 * pour que le poids résiduel du tout premier jour de la fenêtre d'amorçage soit négligeable
 * (même raisonnement que computeACWR, voir ci-dessus).
 */
export function computeACWRSeries(
  loads: TrainingLoadRow[],
  days: number = 30,
  lookbackDays: number = 56
): { date: string; acwr: number }[] {
  if (loads.length === 0) return []

  const totalDays = days + lookbackDays
  const dailySeries = buildDailySeries(loads, totalDays)

  const lambdaAcute = 2 / 8
  const lambdaChronic = 2 / 29

  let acuteEWMA = dailySeries[0].load
  let chronicEWMA = dailySeries[0].load

  const result: { date: string; acwr: number }[] = []
  for (let i = 0; i < dailySeries.length; i++) {
    if (i > 0) {
      acuteEWMA = dailySeries[i].load * lambdaAcute + acuteEWMA * (1 - lambdaAcute)
      chronicEWMA = dailySeries[i].load * lambdaChronic + chronicEWMA * (1 - lambdaChronic)
    }
    if (i >= totalDays - days) {
      const acwr = chronicEWMA > 0 ? acuteEWMA / chronicEWMA : 0
      result.push({ date: dailySeries[i].date, acwr: Math.round(acwr * 100) / 100 })
    }
  }

  return result
}

// ============================================
// STRAIN (charge × monotonie — Foster 1998)
// ============================================
//
// DÉCOUPAGE : semaine CALENDAIRE (lundi→dimanche), pour la tuile comme pour le
// graphe. Avant ce chantier, la tuile calculait sur 7 jours GLISSANTS finissant
// aujourd'hui pendant que le graphe juste en dessous découpait en semaines
// calendaires : deux valeurs différentes sous le même nom, dans le même modal.
// Les deux passent maintenant par `strainFromDailyLoads`, donc la dernière barre
// du graphe EST la valeur de la tuile, par construction et non par coïncidence.

export type StrainLevel = "charge faible" | "optimal" | "vigilance" | "surcharge"

// Nombre minimal de jours chargés pour que la monotonie ait un sens. Sur un seul
// jour, le rapport moyenne/écart-type est un artefact arithmétique, pas une
// contrainte mesurée. Seuil COMMUN à la tuile et au graphe — c'est ce partage qui
// garantit qu'ils ne peuvent plus diverger, y compris dans l'état neutre.
const STRAIN_MIN_LOADED_DAYS = 2

interface StrainMath {
  hasData: boolean
  strain: number
  monotony: number
  weeklyLoad: number
}

/**
 * Formule Foster sur les 7 jours d'UNE semaine.
 *
 * `hasData: false` signifie « pas assez de jours chargés pour que la monotonie
 * veuille dire quelque chose », ce qui n'est PAS la même chose qu'un strain nul.
 * Une semaine sans séance affichait auparavant un vrai 0, qui tombe en zone
 * « Charge faible » (jaune) : l'absence de donnée se lisait comme un verdict.
 */
function strainFromDailyLoads(dayLoads: number[]): StrainMath {
  if (dayLoads.filter((v) => v > 0).length < STRAIN_MIN_LOADED_DAYS) {
    return { hasData: false, strain: 0, monotony: 0, weeklyLoad: 0 }
  }
  const weeklyLoad = dayLoads.reduce((a, b) => a + b, 0)
  const mean = weeklyLoad / 7
  const variance = dayLoads.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / 7
  const std = Math.sqrt(variance)
  const monotony = std > 0 ? mean / std : 0
  return { hasData: true, strain: weeklyLoad * monotony, monotony, weeklyLoad }
}

function loadsToMap(loads: TrainingLoadRow[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const l of loads) m.set(l.date, l.training_load || 0)
  return m
}

// Les 7 charges journalières d'une semaine calendaire, lundi→dimanche. Les jours
// à venir sont absents de `loadMap` donc valent 0, au même titre qu'un jour de
// repos : la semaine en cours est mesurée sur ce qui a déjà eu lieu, et le
// dénominateur reste 7 (la monotonie de Foster se définit sur une semaine pleine).
function weekDailyLoads(loadMap: Map<string, number>, weekStart: Date): number[] {
  const days: number[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart)
    d.setDate(weekStart.getDate() + i)
    days.push(loadMap.get(toLocalISODate(d)) ?? 0)
  }
  return days
}

export interface StrainVerdict {
  hasData: boolean
  strain: number
  monotony: number
  weeklyLoad: number
  level: StrainLevel
  label: string
  color: string
  description: string
}

export interface StrainResult extends StrainVerdict {
  /**
   * Semaine calendaire PRÉCÉDENTE (close), ou null si elle non plus n'est pas
   * exploitable. Sert au bilan quand la semaine en cours n'a pas encore de
   * verdict — cf. computeCurrentStatus. La tuile, elle, affiche toujours la
   * semaine en cours, pour rester alignée sur la dernière barre du graphe.
   */
  lastComplete: StrainVerdict | null
}

function decorateStrain(m: StrainMath): StrainVerdict {
  if (!m.hasData) {
    return {
      hasData: false,
      strain: 0,
      monotony: 0,
      weeklyLoad: 0,
      level: "charge faible",
      label: "Données insuffisantes",
      color: "#94a3b8",
      description: "Besoin d'au moins 2 jours d'entraînement sur la semaine.",
    }
  }

  let level: StrainLevel
  let label: string, color: string, description: string

  if (m.strain < 1500) {
    level = "charge faible"; label = "Charge faible"; color = "#eab308"; description = "Charge contrôlée."
  } else if (m.strain < 3000) {
    level = "optimal"; label = "Optimal"; color = "#22c55e"; description = "Charge bien gérée."
  } else if (m.strain < 4500) {
    level = "vigilance"; label = "Vigilance"; color = "#f97316"; description = "Charge importante. Surveille les signes de fatigue."
  } else {
    level = "surcharge"; label = "Surcharge"; color = "#ef4444"; description = "Risque de surentraînement. Repose-toi."
  }

  return {
    hasData: true,
    strain: Math.round(m.strain),
    monotony: Math.round(m.monotony * 100) / 100,
    weeklyLoad: Math.round(m.weeklyLoad),
    level, label, color, description,
  }
}

/**
 * Strain de la semaine calendaire en cours, plus celui de la semaine close
 * précédente (`lastComplete`).
 *
 * `reference` n'existe que pour les tests — en usage réel c'est toujours aujourd'hui.
 */
export function computeStrain(loads: TrainingLoadRow[], reference: Date = new Date()): StrainResult {
  const loadMap = loadsToMap(loads)
  const thisWeek = startOfWeek(reference)
  const previousWeek = new Date(thisWeek)
  previousWeek.setDate(thisWeek.getDate() - 7)

  const current = decorateStrain(strainFromDailyLoads(weekDailyLoads(loadMap, thisWeek)))
  const previous = decorateStrain(strainFromDailyLoads(weekDailyLoads(loadMap, previousWeek)))

  return { ...current, lastComplete: previous.hasData ? previous : null }
}

/**
 * Série Strain par semaine calendaire (lundi→dimanche) — le graphe du modal.
 * Même `strainFromDailyLoads` que la tuile, donc mêmes chiffres et même seuil.
 *
 * `strain: null` = semaine sans verdict (moins de 2 jours chargés), rendue comme
 * un trou dans le graphe et non comme un point à 0. Une version précédente
 * renvoyait un 0 « réel » pour une semaine à 0 séance, en faisant valoir qu'une
 * semaine vide est une vraie absence de contrainte : c'est vrai en théorie, mais
 * à l'écran ce 0 atterrit en zone jaune « Charge faible », donc l'absence de
 * données se lit comme un verdict de charge. Décision inversée ici.
 */
export function computeStrainByCalendarWeek(
  loads: TrainingLoadRow[],
  weekStart: Date,
  weeksBack: number = 5
): { weekStart: string; strain: number | null }[] {
  const loadMap = loadsToMap(loads)
  const result: { weekStart: string; strain: number | null }[] = []

  for (let w = 0; w <= weeksBack; w++) {
    const wStart = new Date(weekStart)
    wStart.setDate(weekStart.getDate() + w * 7)
    const m = strainFromDailyLoads(weekDailyLoads(loadMap, wStart))
    result.push({
      weekStart: toLocalISODate(wStart),
      strain: m.hasData ? Math.round(m.strain) : null,
    })
  }

  return result
}

// ============================================
// ÉTAT DE FORME — synthèse des 3 indicateurs (5 niveaux)
// ============================================
//
// Ni hebdomadaire ni figé : c'est une photo de l'instant, qui croise un ACWR
// glissant continu, un Strain de semaine calendaire et le Hooper du jour. Aucune
// des trois composantes n'a de cadence hebdomadaire fixe — d'où « État de forme »
// à l'écran comme dans le nom de cette fonction. Le mot « semaine » est réservé à
// la notification du lundi, seul élément réellement hebdomadaire (elle lit
// lundi→dimanche de la semaine close, sans passer par ce fichier).

type Severity = 0 | 1 | 2

export function computeCurrentStatus(
  acwrResult: { zone: AcwrZone; acwr: number },
  strainResult: StrainResult,
  hooper: { score: number; zone: HooperZone } | null
): { level: 1 | 2 | 3 | 4 | 5; label: string; color: string; analysis: string[]; recommendation: string } {
  const r = acwrResult.acwr
  const hooperScore = hooper?.score ?? null
  const hooperZone = hooper?.zone ?? null

  // Le Strain passé en semaine calendaire, la semaine en cours n'a pas de verdict
  // avant sa 2e séance — mécaniquement TOUS les lundis, qui ne contiennent qu'un
  // jour. Mesuré sur les données réelles : à cadence nominale (lun/mar/jeu/ven) le
  // Strain perd sa voix 100 % des lundis, et le Hooper manque 59 % de ces lundis —
  // le bilan serait donc entièrement muet un lundi sur deux, précisément le jour où
  // la notification hebdo renvoie l'utilisateur sur cette page.
  //
  // On fait donc voter la dernière semaine CLOSE tant que la semaine en cours n'a
  // pas de verdict — même principe que `lastComplete` pour Intensité/Volume. La
  // tuile, elle, continue d'afficher la semaine en cours : c'est la puce d'analyse
  // qui précise laquelle des deux parle, plutôt que deux chiffres non étiquetés.
  const votingStrain: StrainVerdict | null = strainResult.hasData ? strainResult : strainResult.lastComplete
  const strainFromLastWeek = !strainResult.hasData && votingStrain !== null
  const s = votingStrain?.strain ?? 0

  const acwrHasData = r > 0
  const strainHasData = votingStrain !== null
  const hooperHasData = hooperZone !== null

  // ACWR "sous-entraînement" n'est pas un drapeau de gravité — il a sa propre famille de cas ci-dessous.
  const acwrSeverity: Severity = acwrResult.zone === "surcharge" ? 2 : acwrResult.zone === "vigilance" ? 1 : 0
  const strainSeverity: Severity =
    votingStrain?.level === "surcharge" ? 2 : votingStrain?.level === "vigilance" ? 1 : 0
  const hooperSeverity: Severity =
    hooperZone === "mauvais" || hooperZone === "très mauvais" ? 2 : hooperZone === "moyen" ? 1 : 0

  const isUndertraining = acwrHasData && acwrResult.zone === "sous-entrainement"

  // Un indicateur sans données n'est ni vert ni orange ni rouge — il ne vote pas dans le comptage.
  // (l'ACWR "pas de données" a acwrHasData=false, donc il est déjà exclu ici, cohérent avec isUndertraining ci-dessus.)
  const knownSeverities: Severity[] = []
  if (acwrHasData) knownSeverities.push(acwrSeverity)
  if (strainHasData) knownSeverities.push(strainSeverity)
  if (hooperHasData) knownSeverities.push(hooperSeverity)
  const knownCount = knownSeverities.length

  const redCount = knownSeverities.filter((v) => v === 2).length
  const orangeCount = knownSeverities.filter((v) => v === 1).length

  // Une puce par indicateur, toujours les trois, dérivée de sa zone réelle — indépendante du niveau global atteint.
  const acwrBullet = (() => {
    if (!acwrHasData) return "ACWR — pas encore de données"
    const descByZone: Record<AcwrZone, string> = {
      "sous-entrainement": "charge insuffisante pour progresser",
      optimal: "zone idéale, progression saine",
      vigilance: "surcharge modérée, vigilance recommandée",
      surcharge: "risque de blessure élevé",
    }
    return `ACWR ${r.toFixed(2)} — ${acwrResult.zone} : ${descByZone[acwrResult.zone]}`
  })()

  const strainBullet = (() => {
    if (votingStrain === null) return "Strain — pas encore de données"
    const byLevel: Record<StrainLevel, { word: string; rest: string }> = {
      "charge faible": { word: "faible", rest: "la charge est absorbée sans difficulté, il reste de la marge" },
      optimal: { word: "soutenable", rest: "le volume est bien réparti sur la semaine, l'organisme encaisse" },
      vigilance: { word: "élevée", rest: "charge concentrée sur peu de séances, la récupération devient le facteur limitant" },
      surcharge: { word: "excessive", rest: "l'organisme subit plus qu'il ne récupère" },
    }
    const { word, rest } = byLevel[votingStrain.level]
    // Étiqueté explicitement : sans ça, le chiffre de la puce contredirait la tuile
    // en début de semaine sans que rien ne l'explique.
    const when = strainFromLastWeek ? " (semaine dernière)" : ""
    return `Strain ${s}${when} — contrainte ${word} : ${rest}`
  })()

  const hooperBullet = (() => {
    if (!hooperHasData || hooperZone === null || hooperScore === null) return "Hooper — pas encore de données"
    const descByZone: Record<HooperZone, string> = {
      excellent: "forme excellente",
      bon: "forme correcte",
      moyen: "fatigue modérée",
      mauvais: "fatigue notable",
      "très mauvais": "risque élevé, repos recommandé",
    }
    return `Hooper ${hooperScore}/28 — ${hooperZone} : ${descByZone[hooperZone]}`
  })()

  const analysis = [acwrBullet, strainBullet, hooperBullet]

  // État neutre : moins de deux indicateurs mesurés (ex. juste après la séance d'essai —
  // ACWR + Hooper disponibles, Strain pas encore, il faut 2 séances sur 7 jours). Pas assez
  // de signal pour calculer un bilan, ce n'est pas une anomalie à signaler comme un "Attention".
  if (knownCount <= 1) {
    return {
      level: 1,
      label: "En attente de données",
      color: "#94a3b8",
      analysis,
      recommendation:
        "Enregistre tes premières séances et remplis ton questionnaire du matin pendant quelques jours. Ton bilan s'activera dès que tes indicateurs seront fiables.",
    }
  }

  // Famille "charge insuffisante" — ACWR sous-entraînement, tant qu'aucun rouge n'est présent.
  if (isUndertraining && redCount === 0) {
    const hooperOrange = hooperSeverity === 1
    const strainOrange = strainSeverity === 1
    let recommendation: string
    if (!hooperOrange && !strainOrange) {
      recommendation =
        "Ta charge récente est nettement inférieure à ta charge de fond : tu perds progressivement le bénéfice du travail accumulé. Remonte par paliers d'environ 10 % par semaine. Ne cherche pas à rattraper d'un coup — c'est le meilleur moyen de basculer en surcharge."
    } else if (hooperOrange && !strainOrange) {
      recommendation =
        "Ta charge est insuffisante pour progresser, mais ton Hooper montre une fatigue modérée. Tu peux augmenter ton volume à condition que ta récupération suive : vise d'abord deux à trois jours de Hooper en zone verte, puis reprends une progression d'environ 10 % par semaine. Augmenter la charge sur une récupération dégradée est le meilleur moyen de passer directement du sous-entraînement à la surcharge."
    } else if (!hooperOrange && strainOrange) {
      recommendation =
        "Ta charge globale est insuffisante, mais la contrainte de cette semaine est déjà élevée — signe que ton volume est concentré sur trop peu de séances. Avant d'augmenter, répartis mieux : ajoute une séance plus légère plutôt que d'alourdir celles que tu fais déjà. En ce moment c'est la répartition, pas le volume, qui limite ta progression."
    } else {
      recommendation =
        "Ta charge de fond a baissé, mais deux signaux restent orange : ta récupération n'est pas au niveau et ta semaine est déjà contraignante. N'augmente pas encore. Stabilise une semaine à charge égale mais mieux répartie, et attends que ton Hooper repasse au vert avant de relancer la progression."
    }
    return { level: 2, label: "Charge insuffisante", color: "#eab308", analysis, recommendation }
  }

  // Comptage de drapeaux — les 10 combinaisons (rouge, orange) possibles, calculées
  // sur les indicateurs disponibles (knownCount >= 2 à ce stade).
  if (redCount === 0 && orangeCount === 0) {
    // Variante B : aucun drapeau, mais le Strain est en zone "charge faible" (pas "optimal") —
    // la tuile Strain s'affiche en jaune, la recommandation doit rester cohérente avec ça.
    const strainLow = votingStrain !== null && votingStrain.level === "charge faible"
    return {
      level: 1,
      label: "Optimal",
      color: "#22c55e",
      analysis,
      recommendation: strainLow
        ? "Aucun de tes indicateurs ne signale de risque, et ta charge hebdomadaire reste modeste : il te reste de la marge. C'est le bon moment pour augmenter progressivement — 5 à 10 % de volume en plus par semaine, en gardant ta fréquence actuelle."
        : "Tes indicateurs sont tous dans le vert. Maintiens ta fréquence actuelle et laisse la charge progresser de 5 à 10 % par semaine au maximum. C'est dans cette zone que la progression est la plus sûre et la plus durable.",
    }
  }

  if (redCount === 0 && orangeCount <= 2) {
    return {
      level: 2,
      label: "Attention",
      color: "#f97316",
      analysis,
      recommendation:
        "Un signal commence à se dégrader sans être alarmant. Garde ta charge actuelle plutôt que de l'augmenter cette semaine, et soigne les leviers de récupération : sommeil, hydratation, apports protéiques. Si le signal persiste au-delà de trois jours, allège d'une séance.",
    }
  }

  if (redCount === 0 && orangeCount === 3) {
    return {
      level: 3,
      label: "Vigilance",
      color: "#f87171",
      analysis,
      recommendation:
        "Aucun signal n'est critique isolément, mais tes trois indicateurs dérivent ensemble — c'est souvent le signe avant-coureur d'une accumulation. Allège d'environ 20 % cette semaine et surveille lequel des trois bascule en premier.",
    }
  }

  if (redCount === 1 && orangeCount <= 1) {
    return {
      level: 3,
      label: "Vigilance",
      color: "#f87171",
      analysis,
      recommendation:
        "Un indicateur est franchement au rouge. Réduis la charge de 20 à 30 % sur tes prochaines séances, en coupant d'abord dans le volume et en préservant l'intensité pour ne pas perdre tes acquis. Réévalue dans trois à quatre jours.",
    }
  }

  if (redCount === 1 && orangeCount === 2) {
    return {
      level: 4,
      label: "Surcharge",
      color: "#ef4444",
      analysis,
      recommendation:
        "Un indicateur est au rouge et les deux autres se dégradent : la fatigue s'installe sur tous les fronts plutôt que sur un point isolé. Réduis la charge d'environ 40 % cette semaine et remplace une séance par de la récupération active — marche, vélo léger ou mobilité, 30 à 40 minutes à faible intensité. Reprends la progression quand au moins deux indicateurs sont revenus au vert.",
    }
  }

  if (redCount === 2) {
    return {
      level: 4,
      label: "Surcharge",
      color: "#ef4444",
      analysis,
      recommendation:
        "Deux indicateurs sont au rouge simultanément, le risque de blessure devient réel. Réduis la charge de 40 à 50 % cette semaine et remplace au moins une séance par de la récupération active — marche, vélo léger ou mobilité, 30 à 40 minutes à faible intensité. Ne reprends la progression que lorsque deux indicateurs au moins sont revenus au vert.",
    }
  }

  // redCount === 3
  return {
    level: 5,
    label: "Alarme surcharge",
    color: "#dc2626",
    analysis,
    recommendation:
      "Tes indicateurs sont tous au rouge : c'est le profil du surentraînement. Arrête l'entraînement 48 à 72 heures. Reprends ensuite par deux à trois jours de récupération active à faible intensité, puis remonte à 50 % de ta charge habituelle.",
  }
}

// ============================================
// PROGRESS — Score de progression
// ============================================
//
// Composantes : Régularité (Hooper, inchangée) + Intensité + Volume
// (computeIntensityTrend/computeVolumeTrend, session_set_logs). Le poids de
// corps n'entre pas dans le score pour l'instant (question séparée). Les
// semaines de décharge ne sont PAS traitées ici — aucun flag deload fiable
// n'existe en base aujourd'hui (uniquement du texte libre dans
// program_sessions.label/format_travail) ; ce sera une tâche séparée plutôt
// que du pattern matching sur ce texte.

// Gaussienne centrée sur +15% (hausse modérée = pic), plancher commun 40 pour
// les deux queues (hausse excessive ET forte baisse) — remplace l'ancien
// barème par paliers, qui punissait une baisse de 1% comme une baisse de 90%,
// et une baisse plus sévèrement qu'une hausse excessive alors que c'est
// l'inverse qui est vrai (cf. ACWR : une progression trop rapide est le vrai
// signal de risque). 70 à trend=0% est le point médian EXACT entre le
// plancher (40) et le plafond (100) — ça fixe σ analytiquement une fois le
// pic choisi à 15%, ce ne sont pas 5 réglages indépendants mais 3 (plancher,
// plafond, pic) + 1 point de passage imposé (stable → 70).
const TREND_SCORE_PEAK_PCT = 15
const TREND_SCORE_FLOOR = 40
const TREND_SCORE_CEILING = 100
const TREND_SCORE_SIGMA = TREND_SCORE_PEAK_PCT / Math.sqrt(2 * Math.LN2)

function trendScore(trendPct: number): number {
  const delta = trendPct - TREND_SCORE_PEAK_PCT
  const gaussian = Math.exp(-(delta * delta) / (2 * TREND_SCORE_SIGMA * TREND_SCORE_SIGMA))
  return TREND_SCORE_FLOOR + (TREND_SCORE_CEILING - TREND_SCORE_FLOOR) * gaussian
}

// Poids de base. Quand Intensité et/ou Volume valent null (pas de semaine de
// référence — état normal pendant les 2 premières semaines d'un compte, pas
// un cas limite), leur poids est redistribué au PRORATA des poids de base
// des composantes restantes : une simple renormalisation du dénominateur,
// pas un branchement séparé par nombre de composantes manquantes. Régularité
// n'est jamais null (toujours un nombre, 0 au minimum) — le dénominateur ne
// peut donc jamais tomber à 0.
const SCORE_WEIGHTS = { regularity: 0.5, intensity: 0.25, volume: 0.25 }

export function computeProgressScore(
  wellness: DailyWellnessRow[],
  intensityTrend: number | null,
  volumeTrend: number | null
): {
  score: number
  regularity: number
} {
  const regularity = Math.min(100, Math.round((wellness.length / 30) * 100))

  const components: { weight: number; score: number }[] = [
    { weight: SCORE_WEIGHTS.regularity, score: regularity },
  ]
  if (intensityTrend !== null) components.push({ weight: SCORE_WEIGHTS.intensity, score: trendScore(intensityTrend) })
  if (volumeTrend !== null) components.push({ weight: SCORE_WEIGHTS.volume, score: trendScore(volumeTrend) })

  const totalWeight = components.reduce((s, c) => s + c.weight, 0)
  const score = Math.round(components.reduce((s, c) => s + c.weight * c.score, 0) / totalWeight)

  return {
    score: Math.max(0, Math.min(100, score)),
    regularity,
  }
}

// Trend d'une composante Intensité/Volume tel qu'il doit ENTRER DANS LE SCORE —
// distinct du trend affiché sur la tuile. Seule la dernière semaine de programme
// COMPLÈTE compte (`lastComplete`) : une semaine en cours n'est pas encore un
// verdict, et le score garde celui de la semaine précédente en attendant, au
// lieu de perdre la composante. Une semaine de reprise post-décharge
// (comparaison à la dernière semaine hors décharge, cf. compareProgrammeWeeks) ne
// doit jamais faire BAISSER le score : suivre une périodisation planifiée n'est
// pas une contre-performance. Trend NÉGATIF de reprise → neutralisé (null →
// composante retirée au prorata, comme avant que la comparaison existe). Trend
// POSITIF de reprise → compté normalement : on a déjà dépassé le niveau d'avant
// la décharge, c'est un vrai gain. Hors reprise, le trend passe tel quel.
// `coverageDegraded` (cf. isCoverageDegraded) : les deux semaines comparées n'ont
// pas été lues avec la même finesse, le pourcentage n'est pas une mesure fiable —
// il ne doit pas entrer dans le score, quel que soit son signe.
export function trendForScore(t: ProgrammeTrend): number | null {
  const c = t.lastComplete
  if (c === null || c.coverageDegraded) return null
  if (c.referenceIsPreDeload && c.trend !== null && c.trend < 0) return null
  return c.trend
}

// ============================================
// INTENSITÉ / VOLUME — Performance décomposée (session_set_logs)
// ============================================
//
// Ces deux indicateurs lisent session_set_logs — l'exécution réelle série
// par série, capturée par SessionPage à la fin de chaque séance (voir
// migration 20260825120000_session_set_logs.sql). Leurs `trend` alimentent
// directement computeProgressScore ci-dessus (composantes Intensité/Volume).
//
// COUVERTURE : `reps` et `tempo` sont des champs texte libres très hétérogènes
// dans le catalogue (clusters, drop sets, AMRAP, myo-reps, tempos combinés...).
// On ne tente de parser QUE les formats simples ci-dessous ; tout le reste est
// exclu de la somme (jamais une valeur inventée) — voir `parseRepsCount` /
// `parseHoldSeconds` / `parseTempoSecondsPerRep`. Une semaine en périodisation
// avancée (clusters/myo-reps) aura donc une couverture plus faible qu'une
// semaine en séries simples : le chiffre est un plancher, pas un total exact.
// `coverage` (retourné par les deux fonctions + loggé en console) expose le
// %, pour distinguer une vraie stagnation d'un artefact de parsing.

// Calibrés sur une séance normale complète réelle (programme
// beginner_prise-de-muscle_gym_male, séance 32 — cf. discussion produit),
// une fois `sets_count` appliqué : ~11 360 kg / ~1288 s pour CETTE séance.
// Seuil ≈ la moitié de ce total : assez bas pour ne pas pénaliser une semaine
// allégée, assez haut pour qu'une semaine quasi vide ne produise pas un % absurde.
// Ces valeurs remplacent 1500/300 (calibrées avant l'ajout de `sets_count`, sur
// des totaux qui ne comptaient qu'une série par exercice — sous-évalués d'un facteur
// équivalent au nombre de séries prescrites, ~4 pour cette séance de référence).
const MIN_REFERENCE_INTENSITY_KG = 5500
const MIN_REFERENCE_VOLUME_SECONDS = 600

export interface CoverageStats {
  total: number // lignes dans le périmètre (charge_basis) de l'indicateur
  counted: number // lignes ayant réellement contribué à la somme
  excluded: number // reps/tempo non standard, ou charge_kg manquant
  coveragePct: number // 100 par convention si total === 0 (rien à exclure)
}

function computeCoverage(total: number, counted: number): CoverageStats {
  return {
    total,
    counted,
    excluded: total - counted,
    coveragePct: total > 0 ? Math.round((counted / total) * 100) : 100,
  }
}

// Garde-fou de comparabilité. Le trend divise deux sommes dont la couverture peut
// différer : 55 % contre 40 % a suffi à afficher +222 % là où la progression réelle
// était +130 %, parce que la semaine de référence perdait ses mouvements les plus
// lourds (notations non parsées) davantage que la semaine en cours.
//
// Deux conditions CUMULATIVES, qui répondent à deux questions distinctes :
//   - plancher → « a-t-on vu assez de chaque semaine ? »
//   - écart    → « a-t-on vu la même chose des deux côtés ? »
// Une couverture basse mais SYMÉTRIQUE (45 %/45 %) reste comparable : le biais se
// compense largement, on ne dégrade pas — sans quoi l'indicateur s'éteindrait en
// permanence sur les programmes avancés, dont la couverture Volume est
// structurellement basse (médiane catalogue 67 % sur les séances appariées,
// plombée surtout par les reps non parsées : drop sets, fourchettes "8-10",
// notations composites).
//
// Calibré sur les 290 semaines de programme du catalogue, hors décharge (1458
// séances — lecture paginée) : déclenche sur 20 % des paires en Intensité, 43 %
// en Volume. Les trends ainsi masqués sont médians à −46 % / −25 % (extrêmes
// +143 % / −95 %) : ce sont bien des comparaisons faussées, pas du bruit. Le taux
// Volume élevé vient de la couverture elle-même, pas du seuil. Mesuré de nouveau
// en semaines de programme après prise en compte du X dans les tempos : 38 % en
// Volume (40 % avant) — le reste vient des reps, pas du tempo.
const COVERAGE_FLOOR_PCT = 60
const COVERAGE_GAP_PCT = 10

function isCoverageDegraded(current: CoverageStats, previous: CoverageStats): boolean {
  const lowest = Math.min(current.coveragePct, previous.coveragePct)
  const gap = Math.abs(current.coveragePct - previous.coveragePct)
  return lowest < COVERAGE_FLOOR_PCT && gap >= COVERAGE_GAP_PCT
}

// "10", "12/côté", "8/jambe", "6/bras" — /côté /jambe /bras comptés ×2 (les
// deux côtés sont faits dans la même série/tap, donc le temps sous tension et
// le tonnage réels couvrent bien les deux).
const SIMPLE_REPS_RE = /^(\d+)(\/(côté|jambe|bras))?$/

// "10+4+3", "8+3+2/jambe" — REST-PAUSE (ou cluster) : segments d'UNE même série,
// à charge CONSTANTE (catalogue : `sets: "3 RP"`/"4 RP", méthode de cycle
// "Rest-Pause 10+4+3"). Les segments sont donc sommables tels quels : le tonnage
// vaut (10+4+3) × charge, et le temps sous tension (10+4+3) × tempo.
//
// Le `+` doit être COLLÉ. Espacé ("10 + 10"), c'est la notation des SUPERSETS —
// deux mouvements distincts, normalement déjà séparés à l'écriture par
// splitCombinedField ; en sommer les segments appliquerait la charge d'un seul
// mouvement aux répétitions des deux.
//
// Les drop sets ("12→10→8", catalogue : `sets: "1 DS"`/"3 drops", "Drop Sets
// dégressifs") restent volontairement exclus : seul le premier segment est à
// `charge_kg`, les suivants à des charges dégressives que la donnée ne porte pas.
// Les formes composites ("15/12/10/(8+3×3)", "3+3+3 (=9)") restent exclues aussi,
// l'ancrage strict de la regex s'en charge.
const REST_PAUSE_REPS_RE = /^(\d+(?:\+\d+)+)(\/(côté|jambe|bras))?$/

function parseRepsCount(reps: string): number | null {
  const trimmed = reps.trim()

  const simple = SIMPLE_REPS_RE.exec(trimmed)
  if (simple) {
    const n = parseInt(simple[1], 10)
    return simple[2] ? n * 2 : n
  }

  const restPause = REST_PAUSE_REPS_RE.exec(trimmed)
  if (restPause) {
    const total = restPause[1].split("+").reduce((s, seg) => s + parseInt(seg, 10), 0)
    return restPause[2] ? total * 2 : total
  }

  return null
}

// "45''" — maintien isométrique (gainage/planche) : la valeur EST déjà une
// durée en secondes, pas un nombre de répétitions à multiplier par un tempo
// (qui n'a pas de sens pour un maintien statique).
const HOLD_REPS_RE = /^(\d+)''$/

function parseHoldSeconds(reps: string): number | null {
  const m = HOLD_REPS_RE.exec(reps.trim())
  return m ? parseInt(m[1], 10) : null
}

// Tempo 4 phases strict (descente, pause basse, montée, pause haute), chaque
// phase étant un chiffre en secondes OU "X" — exécution explosive, comptée
// 1 seconde : "3010" → 3+0+1+0 = 4 s/rep, "30X0" → 3+0+1+0 = 4 s/rep, "X0X0" →
// 2 s/rep. Sans le X, ~590 lignes du catalogue à reps parfaitement lisibles
// ("30X0", "20X1", "20X0"…) sortaient du Volume, faisant baisser la couverture
// et déclencher « mesure partielle » à tort.
// Toujours exclus : "rapide"/"iso"/"—", tempos à 3 phases ("1-2-2") et combinés
// ("3010 / 2010") — cf. decodeTempo (types/session.ts).
const TEMPO_RE = /^[\dX]{4}$/i
const EXPLOSIVE_PHASE_SECONDS = 1

function parseTempoSecondsPerRep(tempo: string | null): number | null {
  if (!tempo) return null
  const t = tempo.trim()
  if (!TEMPO_RE.test(t)) return null
  return t
    .split("")
    .reduce((s, phase) => s + (/x/i.test(phase) ? EXPLOSIVE_PHASE_SECONDS : parseInt(phase, 10)), 0)
}

// Profondeur max de la marche arrière (en semaines de PROGRAMME) pour trouver une
// semaine de référence "propre" (ni décharge, ni sous le seuil plancher), et
// pour retrouver la dernière semaine complète. Au-delà : pas de comparaison.
const MAX_REFERENCE_LOOKBACK_WEEKS = 4

// Comparaison d'UNE semaine de programme à sa référence.
export interface WeekComparison {
  week: number // program_sessions.week
  referenceWeek: number | null
  total: number // somme brute de la semaine (toutes séances, avant appariement)
  trend: number | null
  isDeloadWeek: boolean
  referenceIsPreDeload: boolean
  coverageDegraded: boolean
  sessionsDone: number
  sessionsPrescribed: number
  // Toutes les séances prescrites de la semaine sont loggées, OU une semaine
  // ultérieure a commencé (séance sautée : la semaine est close quand même).
  complete: boolean
  coverage: { current: CoverageStats; previous: CoverageStats }
}

// `current` : semaine de programme la plus avancée ayant au moins une séance
// loggée — éventuellement incomplète (ce qu'affiche la tuile).
// `lastComplete` : semaine complète la plus récente — `current` elle-même si elle
// est complète. C'est son verdict qui entre dans le score (cf. trendForScore),
// pour que le score ne perde pas Intensité/Volume pendant toute la semaine en cours.
export interface ProgrammeTrend {
  current: WeekComparison | null
  lastComplete: WeekComparison | null
}

type SumWeek = (rows: SessionSetLogRow[]) => { sum: number; coverage: CoverageStats }

// Appariement par TYPE de séance : on ne compare que les séances présentes des
// deux côtés (le Push de cette semaine au Push de la référence…). Une séance
// sans homologue — typiquement un Testing, qui remplace une séance du split en
// début/fin de programme — est retirée des DEUX sommes (et de la couverture), au
// lieu de faire comparer une séance à une séance d'une autre nature. Le type est
// unique au sein d'une semaine de programme : pas besoin de rang d'occurrence.
// Une séance sans type (aucune ligne catalogue) n'est jamais appariable.
function matchSessions(
  current: SessionSetLogRow[],
  reference: SessionSetLogRow[]
): { current: SessionSetLogRow[]; reference: SessionSetLogRow[] } {
  const typesOf = (rows: SessionSetLogRow[]) =>
    new Set(rows.map((r) => r.session_type).filter((t): t is string => t !== null))
  const referenceTypes = typesOf(reference)
  const common = new Set([...typesOf(current)].filter((t) => referenceTypes.has(t)))
  const keep = (r: SessionSetLogRow) => r.session_type !== null && common.has(r.session_type)
  return { current: current.filter(keep), reference: reference.filter(keep) }
}

function compareProgrammeWeeks(
  logs: SessionSetLogRow[],
  plan: ProgrammePlanSession[],
  inScope: (r: SessionSetLogRow) => boolean,
  sumWeek: SumWeek,
  minReference: number
): ProgrammeTrend {
  const byWeek = new Map<number, SessionSetLogRow[]>()
  for (const r of logs) {
    if (r.programme_week === null) continue
    const rows = byWeek.get(r.programme_week) ?? []
    rows.push(r)
    byWeek.set(r.programme_week, rows)
  }
  if (byWeek.size === 0) return { current: null, lastComplete: null }

  const prescribed = new Map<number, number>()
  const deloadWeeks = new Set<number>()
  for (const s of plan) {
    prescribed.set(s.week, (prescribed.get(s.week) ?? 0) + 1)
    if (s.is_deload) deloadWeeks.add(s.week)
  }
  const currentWeek = Math.max(...byWeek.keys())

  // Semaine de décharge = définie par le CATALOGUE (au moins une séance
  // is_deload dans la semaine de programme), plus par le calendrier : une séance
  // décalée ne peut plus faire basculer une semaine voisine en décharge.
  const compare = (week: number): WeekComparison => {
    const rows = byWeek.get(week) ?? []
    const isDeloadWeek = deloadWeeks.has(week)
    const total = sumWeek(rows.filter(inScope))
    const sessionsDone = new Set(rows.map((r) => r.programme_session_number)).size
    const sessionsPrescribed = prescribed.get(week) ?? sessionsDone
    const complete = sessionsDone >= sessionsPrescribed || week < currentWeek

    // Référence : semaine de programme précédente, en sautant les décharges
    // (→ referenceIsPreDeload) et les semaines sous le plancher (semaine sautée
    // ou à peine entamée).
    let reference: { week: number; rows: SessionSetLogRow[]; preDeload: boolean } | null = null
    let skippedDeload = false
    for (let back = 1; back <= MAX_REFERENCE_LOOKBACK_WEEKS && week - back >= 1; back++) {
      const w = week - back
      if (deloadWeeks.has(w)) {
        skippedDeload = true
        continue
      }
      const refRows = byWeek.get(w) ?? []
      if (sumWeek(refRows.filter(inScope)).sum < minReference) continue
      reference = { week: w, rows: refRows, preDeload: skippedDeload }
      break
    }

    const base = { week, total: total.sum, isDeloadWeek, sessionsDone, sessionsPrescribed, complete }
    if (reference === null) {
      return {
        ...base,
        referenceWeek: null,
        trend: null,
        referenceIsPreDeload: false,
        coverageDegraded: false,
        coverage: { current: total.coverage, previous: computeCoverage(0, 0) },
      }
    }

    const matched = matchSessions(rows, reference.rows)
    const w1 = sumWeek(matched.current.filter(inScope))
    const w2 = sumWeek(matched.reference.filter(inScope))
    // Aucune séance appariée (seul un Testing fait) : pas de comparaison, plutôt
    // qu'un −100 % mécanique.
    const trend =
      isDeloadWeek || matched.current.length === 0 || w2.sum <= 0
        ? null
        : Math.round(((w1.sum - w2.sum) / w2.sum) * 100)
    return {
      ...base,
      referenceWeek: reference.week,
      trend,
      referenceIsPreDeload: reference.preDeload,
      coverageDegraded: trend !== null && isCoverageDegraded(w1.coverage, w2.coverage),
      coverage: { current: w1.coverage, previous: w2.coverage },
    }
  }

  const current = compare(currentWeek)
  let lastComplete: WeekComparison | null = current.complete ? current : null
  for (let back = 1; lastComplete === null && back <= MAX_REFERENCE_LOOKBACK_WEEKS; back++) {
    if (byWeek.has(currentWeek - back)) lastComplete = compare(currentWeek - back)
  }
  return { current, lastComplete }
}

function logTrend(tag: string, t: ProgrammeTrend, excludedReason: string): void {
  const describe = (c: WeekComparison) =>
    `S${c.week}${c.referenceWeek !== null ? ` vs S${c.referenceWeek}` : ""} ` +
    `(${c.sessionsDone}/${c.sessionsPrescribed} séances${c.complete ? ", complète" : ""}) : ` +
    `${c.trend === null ? "—" : `${c.trend} %`}, ${c.coverage.current.counted}/${c.coverage.current.total} séries ` +
    `appariées comptées (${c.coverage.current.coveragePct}%, ${c.coverage.current.excluded} exclues : ${excludedReason})` +
    (c.isDeloadWeek ? ", décharge" : "") +
    (c.referenceIsPreDeload ? ", référence d'avant décharge" : "") +
    (c.coverageDegraded ? `, couverture non comparable (vs ${c.coverage.previous.coveragePct}%)` : "")
  if (t.current === null) return console.info(`[${tag}] aucune série loggée dans le programme en cours.`)
  console.info(
    `[${tag}] en cours ${describe(t.current)}` +
    (t.lastComplete && t.lastComplete !== t.current ? ` · dernière complète ${describe(t.lastComplete)}` : "")
  )
}

// Périmètre de chaque indicateur, extrait pour être réutilisable tel quel par
// une somme sur une fenêtre autre qu'une semaine de programme (bilan de fin
// d'essai) : le même filtre et le même parsing des deux côtés, sinon les
// chiffres du bilan ne voudraient pas dire la même chose que ceux du Monitoring.
const intensityInScope = (r: SessionSetLogRow) =>
  r.charge_basis === "external" || r.charge_basis === "bodyweight_net"

const volumeInScope = (r: SessionSetLogRow) => r.charge_basis !== "not_applicable"

/** Charge soulevée, en kg : somme de charge × répétitions × séries. */
const sumIntensity: SumWeek = (rows) => {
  let sum = 0
  let counted = 0
  for (const r of rows) {
    const reps = parseRepsCount(r.reps)
    if (reps === null || r.charge_kg === null) continue
    const kg = r.per_dumbbell ? r.charge_kg * 2 : r.charge_kg
    sum += kg * reps * r.sets_count
    counted++
  }
  return { sum, coverage: computeCoverage(rows.length, counted) }
}

/** Temps sous tension, en secondes : maintiens isométriques, sinon reps × tempo. */
const sumVolume: SumWeek = (rows) => {
  let sum = 0
  let counted = 0
  for (const r of rows) {
    const holdSeconds = parseHoldSeconds(r.reps)
    if (holdSeconds !== null) {
      sum += holdSeconds * r.sets_count
      counted++
      continue
    }
    const reps = parseRepsCount(r.reps)
    const tempoSecPerRep = parseTempoSecondsPerRep(r.tempo)
    if (reps === null || tempoSecPerRep === null) continue
    sum += reps * tempoSecPerRep * r.sets_count
    counted++
  }
  return { sum, coverage: computeCoverage(rows.length, counted) }
}

/**
 * Sommes BRUTES d'un ensemble de séries, sans comparaison ni référence.
 *
 * C'est tout ce qu'on peut dire honnêtement de 7 jours : les tendances
 * Intensité/Volume divisent deux semaines de programme, et la première semaine
 * n'en a pas de précédente. Le bilan de fin d'essai n'affiche donc que ces deux
 * totaux, sans pourcentage.
 *
 * `coverage` dit combien de séries ont réellement pu être comptées (reps ou
 * tempo non standard : drop sets, fourchettes « 8-10 »…) — à afficher quand
 * elle est basse, pour que le total ne se lise pas comme un compte exhaustif.
 */
export function sumAbsoluteWork(logs: SessionSetLogRow[]): {
  intensityKg: number
  volumeSeconds: number
  intensityCoverage: CoverageStats
  volumeCoverage: CoverageStats
} {
  const intensity = sumIntensity(logs.filter(intensityInScope))
  const volume = sumVolume(logs.filter(volumeInScope))
  return {
    intensityKg: Math.round(intensity.sum),
    volumeSeconds: Math.round(volume.sum),
    intensityCoverage: intensity.coverage,
    volumeCoverage: volume.coverage,
  }
}

export function computeIntensityTrend(logs: SessionSetLogRow[], plan: ProgrammePlanSession[]): ProgrammeTrend {
  const t = compareProgrammeWeeks(logs, plan, intensityInScope, sumIntensity, MIN_REFERENCE_INTENSITY_KG)
  logTrend("computeIntensityTrend", t, "reps non standard ou charge_kg manquant")
  return t
}

export function computeVolumeTrend(logs: SessionSetLogRow[], plan: ProgrammePlanSession[]): ProgrammeTrend {
  const t = compareProgrammeWeeks(logs, plan, volumeInScope, sumVolume, MIN_REFERENCE_VOLUME_SECONDS)
  logTrend("computeVolumeTrend", t, "reps/tempo non standard")
  return t
}
